import type { ExtractAtomValue } from 'jotai'
import type { PartialDeep } from 'type-fest'

import { queryOptions } from '@tanstack/react-query'
import { pipe } from 'effect'
import { identity } from 'es-toolkit'
import { atom } from 'jotai'
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query'
import { atomWithStorage } from 'jotai/utils'
import ms from 'ms'
import { ofetch } from 'ofetch'
import createFetchClient from 'openapi-fetch'
import createClient from 'openapi-react-query'
import PQueue from 'p-queue'

import type { os } from '@sub-vocab/utils/types'

import { env } from '@/env'
import { bindApply, downloadFile, omitUndefined } from '@sub-vocab/utils/lib'

const baseUrl = env.NEXT_PUBLIC_SUB_API_URL

const OS_MAX_RETRIES = 4

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const statusCode = Reflect.get(error, 'statusCode')
  if (typeof statusCode === 'number') {
    return statusCode
  }

  const status = Reflect.get(error, 'status')
  if (typeof status === 'number') {
    return status
  }

  const response = Reflect.get(error, 'response')
  if (!response || typeof response !== 'object') {
    return undefined
  }

  const responseStatus = Reflect.get(response, 'status')
  if (typeof responseStatus === 'number') {
    return responseStatus
  }
}

const shouldRetryOsRequest = (failureCount: number, error: unknown) => {
  if (failureCount >= OS_MAX_RETRIES) {
    return false
  }

  const statusCode = getErrorStatusCode(error)

  if (statusCode === undefined) {
    return true
  }

  if (statusCode >= 400 && statusCode < 500) {
    return false
  }

  return statusCode >= 500
}

const fetchClient = createFetchClient<os.paths>({
  baseUrl: `${baseUrl}/opensubtitles-proxy/def`,
})
export const $osApi = createClient(fetchClient)

export const osSessionAtom = atomWithStorage<PartialDeep<Login['Response']> | undefined>('osSessionAtom', undefined)

export const opensubtitlesAuthorizationAtom = atom((get) => {
  const osSession = get(osSessionAtom)
  if (osSession?.token) {
    return `Bearer ${osSession.token}`
  }
})

export const opensubtitlesReqAtom = atom((get) => {
  const osSession = get(osSessionAtom)

  if (osSession?.user?.vip) {
    const Authorization = get(opensubtitlesAuthorizationAtom)
    return {
      baseUrl: `${env.NEXT_PUBLIC_SUB_API_URL}/opensubtitles-proxy/vip`,
      headers: omitUndefined({
        Authorization,
      }),
    }
  }

  return {
    baseUrl: `${env.NEXT_PUBLIC_SUB_API_URL}/opensubtitles-proxy/def`,
    headers: {
    },
  }
})

/*
  * https://opensubtitles.stoplight.io/docs/opensubtitles-api/a172317bd5ccc-search-for-subtitles
  */
export type Subtitles = {
  Query: NonNullable<os.paths['/subtitles']['get']['parameters']['query']>
  Response: os.paths['/discover/most_downloaded']['get']['responses'][200]['content']['application/json']
}

export const openSubtitlesQueryOptionsAtom = atom((get) => {
  const { baseUrl, headers } = get(opensubtitlesReqAtom)
  return (query: Subtitles['Query']) => {
    // https://opensubtitles.stoplight.io/docs/opensubtitles-api/6ef2e232095c7-best-practices
    const sortedQuery = pipe(
      Object.entries(query).sort(),
      (x) => Object.fromEntries(x),
    )
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return queryOptions({
      queryKey: ['opensubtitles-subtitles', sortedQuery],
      queryFn: () => ofetch<Subtitles['Response']>(`${baseUrl}/subtitles`, {
        method: 'GET',
        query: sortedQuery,
        headers,
      }),
      select: (data) => data.data,
      retry: shouldRetryOsRequest,
    })
  }
})

export const openSubtitlesSubtitlesAtom = (query: Subtitles['Query']) => atomWithQuery((get) => get(openSubtitlesQueryOptionsAtom)(query))

export type SubtitleResponseData = NonNullable<ExtractAtomValue<ReturnType<typeof openSubtitlesSubtitlesAtom>>['data']>[number]

/*
  * https://opensubtitles.stoplight.io/docs/opensubtitles-api/73acf79accc0a-login
  */
export type Login = {
  Body: NonNullable<os.paths['/login']['post']['requestBody']>['content']['application/json']
  Response: os.paths['/login']['post']['responses'][200]['content']['application/json']
}

/*
  * https://opensubtitles.stoplight.io/docs/opensubtitles-api/6be7f6ae2d918-download
  */
export type Download = {
  Body: NonNullable<os.paths['/download']['post']['requestBody']>['content']['application/json']
  Response: os.paths['/download']['post']['responses'][200]['content']['application/json']
}

const osQueue = new PQueue({
  concurrency: 20,
  interval: ms('1s'),
  intervalCap: 20,
  carryoverConcurrencyCount: true,
})

const withHighPriorityOsQueue = <A extends any[], R>(f: (...a: A) => R) => (...a: A) => {
  return osQueue.add(() => f(...a), {
    // https://github.com/tokyodrift1993/p-queue/commit/e48716fa1236fdd8ac0ad88f7831467f2455976a
  })
}

const withNormalPriorityOsQueue = <A extends any[], R>(f: (...a: A) => R) => (...a: A) => {
  return osQueue.add(() => f(...a), {
    // https://github.com/tokyodrift1993/p-queue/commit/e48716fa1236fdd8ac0ad88f7831467f2455976a
    priority: 2,
  })
}

const requestSubtitleURLAtom = atomWithMutation((get) => {
  return {
    mutationKey: ['requestSubtitleURLAtom'],
    mutationFn: (body: Download['Body']) => ofetch<Download['Response']>(`${get(opensubtitlesReqAtom).baseUrl}/download`, {
      method: 'POST',
      body,
      headers: omitUndefined({
        Authorization: get(opensubtitlesAuthorizationAtom),
      }),
    }),
    retry: shouldRetryOsRequest,
  }
})

const fileAtom = atomWithMutation(() => {
  return {
    mutationKey: ['fileAtom'] as const,
    mutationFn: identity(bindApply(ofetch<string, 'text'>)),
    retry: shouldRetryOsRequest,
  }
})

const downloadFileByLinkAtom = atomWithMutation(() => {
  return {
    mutationKey: ['downloadFileByLinkAtom'] as const,
    mutationFn: identity(bindApply(downloadFile)),
    retry: shouldRetryOsRequest,
  }
})

export const openSubtitlesTextAtom = atomWithMutation((get) => {
  return {
    mutationKey: ['openSubtitlesTextAtom'] as const,
    mutationFn: async (body: Download['Body']) => {
      const file = await withHighPriorityOsQueue(get(requestSubtitleURLAtom).mutateAsync)(body)
      const text = await withNormalPriorityOsQueue(get(fileAtom).mutateAsync)([file.link, { responseType: 'text' }])
      return {
        file,
        text,
      }
    },
    retry: shouldRetryOsRequest,
  }
})

export const openSubtitlesDownloadAtom = atomWithMutation((get) => {
  return {
    mutationKey: ['openSubtitlesDownloadAtom'] as const,
    mutationFn: async (body: Download['Body']) => {
      const file = await withHighPriorityOsQueue(get(requestSubtitleURLAtom).mutateAsync)(body)
      await withNormalPriorityOsQueue(get(downloadFileByLinkAtom).mutateAsync)([file.link, file.file_name])
    },
    retry: shouldRetryOsRequest,
  }
})
